'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { toast } from 'sonner';
import AppImage from '@/components/ui/AppImage';
import StatusBadge from '@/components/ui/StatusBadge';
import { events } from '@/lib/mockData';
import {
  CalendarDays, MapPin, Users, Clock, ChevronLeft,
  Minus, Plus, Ticket, Share2, Heart, ExternalLink, CheckCircle2, X } from
'lucide-react';
import Icon from '@/components/ui/AppIcon';


const event = events[0]; // React Summit 2026 as detail view
const relatedEvents = events.filter((e) => e.categoryId === event.categoryId && e.id !== event.id);

function formatDate(dateStr: string) {
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const [y, m, d] = dateStr.split('-');
  return `${months[parseInt(m) - 1]} ${parseInt(d)}, ${y}`;
}

type TicketType = 'general' | 'vip';

export default function EventDetailContent() {
  const [ticketType, setTicketType] = useState<TicketType>('general');
  const [quantity, setQuantity] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [wishlist, setWishlist] = useState(false);

  const price = ticketType === 'vip' ? event.vipPrice ?? event.ticketPrice : event.ticketPrice;
  const total = price * quantity;

  const handleRegister = async () => {
    setIsRegistering(true);
    // Backend integration: POST /api/registrations { eventId, ticketType, quantity }
    await new Promise((r) => setTimeout(r, 1500));
    setIsRegistering(false);
    setShowModal(true);
  };

  return (
    <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-8">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
        <Link href="/event-catalog" className="flex items-center gap-1 hover:text-primary transition-colors">
          <ChevronLeft size={14} />
          Browse Events
        </Link>
        <span>/</span>
        <span className="text-foreground font-medium truncate">{event.title}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main content */}
        <div className="lg:col-span-2 flex flex-col gap-8">
          {/* Hero image */}
          <div className="relative h-72 sm:h-96 rounded-2xl overflow-hidden shadow-card">
            <AppImage
              src={event.imageUrl}
              alt={`${event.title} event banner — ${event.venue}, ${event.city}`}
              fill
              className="object-cover"
              priority
              sizes="(max-width: 1024px) 100vw, 66vw" />
            
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            <div className="absolute bottom-4 left-4 flex items-center gap-2">
              <span className="text-xs font-semibold px-3 py-1.5 bg-white/90 backdrop-blur-sm text-primary rounded-full">
                {event.category}
              </span>
              <StatusBadge status={event.status} />
            </div>
            <div className="absolute top-4 right-4 flex gap-2">
              <button
                onClick={() => {setWishlist(!wishlist);toast(wishlist ? 'Removed from wishlist' : 'Added to wishlist');}}
                className={`p-2.5 rounded-xl backdrop-blur-sm transition-all ${wishlist ? 'bg-danger text-white' : 'bg-white/80 text-muted-foreground hover:text-danger'}`}
                aria-label="Add to wishlist">
                
                <Heart size={17} fill={wishlist ? 'currentColor' : 'none'} />
              </button>
              <button
                onClick={() => {toast('Link copied to clipboard!');}}
                className="p-2.5 rounded-xl bg-white/80 backdrop-blur-sm text-muted-foreground hover:text-foreground transition-all"
                aria-label="Share event">
                
                <Share2 size={17} />
              </button>
            </div>
          </div>

          {/* Title + metadata */}
          <div>
            <h1 className="text-3xl font-extrabold text-foreground mb-3 leading-tight">{event.title}</h1>
            <div className="flex flex-wrap gap-2 mb-5">
              {event.tags.map((tag) =>
              <span key={`tag-${tag}`} className="text-xs font-semibold px-3 py-1 bg-secondary text-secondary-foreground rounded-full">
                  #{tag}
                </span>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-5 bg-card rounded-2xl border border-border">
              {[
              { icon: CalendarDays, label: 'Date', value: formatDate(event.date) },
              { icon: Clock, label: 'Time', value: `${event.time} – ${event.endTime}` },
              { icon: MapPin, label: 'Venue', value: event.venue },
              { icon: Users, label: 'Seats remaining', value: `${event.seatsRemaining} of ${event.totalSeats}` }].
              map((item) => {
                const Icon = item.icon;
                return (
                  <div key={`meta-${item.label}`} className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-xl bg-secondary flex items-center justify-center shrink-0 mt-0.5">
                      <Icon size={16} className="text-primary" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{item.label}</p>
                      <p className="text-sm font-semibold text-foreground">{item.value}</p>
                    </div>
                  </div>);

              })}
            </div>
          </div>

          {/* Description */}
          <div>
            <h2 className="text-xl font-bold text-foreground mb-4">About this event</h2>
            <div className="prose prose-sm max-w-none text-muted-foreground leading-relaxed">
              <p>{event.description}</p>
              <p className="mt-4">
                Whether you&apos;re a senior engineer looking to stay current or a junior developer leveling up, React Summit delivers actionable sessions, hands-on workshops, and networking with 600+ attendees from across the industry.
              </p>
              <p className="mt-4">
                General admission includes full conference access, lunch, and a swag bag. VIP tickets add access to the speakers&apos; dinner, exclusive workshops, and front-row seating.
              </p>
            </div>
          </div>

          {/* Organizer card */}
          <div className="p-5 bg-card rounded-2xl border border-border">
            <h2 className="text-base font-bold text-foreground mb-4">Organized by</h2>
            <div className="flex items-start gap-4">
              <AppImage
                src="https://img.rocket.new/generatedImages/rocket_gen_img_1216c049b-1763298495846.png"
                alt="TechForward Events organizer profile photo — professional headshot"
                width={52}
                height={52}
                className="rounded-full shrink-0" />
              
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <p className="font-bold text-foreground text-sm">TechForward Events</p>
                  <span className="text-xs font-semibold px-2 py-0.5 bg-success-bg text-success rounded-full">✓ Verified</span>
                </div>
                <p className="text-xs text-muted-foreground mb-3">Producing world-class tech conferences and developer workshops since 2018.</p>
                <div className="flex gap-4 text-xs text-muted-foreground">
                  <span><strong className="text-foreground">5</strong> events created</span>
                  <span><strong className="text-foreground">$48,200</strong> in sales</span>
                </div>
              </div>
              <button className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-primary bg-secondary rounded-lg hover:opacity-80 transition-all">
                <ExternalLink size={13} />
                View Profile
              </button>
            </div>
          </div>

          {/* Location */}
          <div>
            <h2 className="text-xl font-bold text-foreground mb-4">Location</h2>
            <div className="p-5 bg-card rounded-2xl border border-border">
              <div className="flex items-center gap-3 mb-3">
                <MapPin size={16} className="text-primary" />
                <div>
                  <p className="font-semibold text-foreground text-sm">{event.venue}</p>
                  <p className="text-xs text-muted-foreground">{event.location}</p>
                </div>
              </div>
              <div className="h-40 bg-muted rounded-xl flex items-center justify-center text-muted-foreground text-sm">
                Map view — Moscone Center, San Francisco
              </div>
            </div>
          </div>
        </div>

        {/* Sticky ticket sidebar */}
        <div className="lg:col-span-1">
          <div className="sticky top-20">
            <div className="bg-card border border-border rounded-2xl shadow-card overflow-hidden">
              {/* Ticket type selector */}
              <div className="p-5 border-b border-border">
                <h3 className="font-bold text-foreground mb-4">Select Tickets</h3>
                <div className="flex flex-col gap-3">
                  <button
                    onClick={() => setTicketType('general')}
                    className={`flex items-center justify-between p-3.5 rounded-xl border-2 transition-all duration-150 ${
                    ticketType === 'general' ? 'border-primary bg-secondary' : 'border-border hover:border-primary/40'}`
                    }>
                    
                    <div className="text-left">
                      <p className="text-sm font-semibold text-foreground">General Admission</p>
                      <p className="text-xs text-muted-foreground">Full conference access + lunch</p>
                    </div>
                    <span className="text-lg font-extrabold text-foreground tabular-nums">${event.ticketPrice}</span>
                  </button>
                  {event.vipPrice &&
                  <button
                    onClick={() => setTicketType('vip')}
                    className={`flex items-center justify-between p-3.5 rounded-xl border-2 transition-all duration-150 ${
                    ticketType === 'vip' ? 'border-accent bg-warning-bg' : 'border-border hover:border-accent/40'}`
                    }>
                    
                      <div className="text-left">
                        <p className="text-sm font-semibold text-foreground">VIP Access</p>
                        <p className="text-xs text-muted-foreground">Front row + speakers&apos; dinner</p>
                      </div>
                      <span className="text-lg font-extrabold text-foreground tabular-nums">${event.vipPrice}</span>
                    </button>
                  }
                </div>
              </div>

              {/* Quantity */}
              <div className="p-5 border-b border-border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-foreground">Quantity</p>
                    <p className="text-xs text-muted-foreground">Max 4 per order</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      disabled={quantity <= 1}
                      className="w-8 h-8 rounded-lg border border-border flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40 transition-all"
                      aria-label="Decrease quantity">
                      
                      <Minus size={14} />
                    </button>
                    <span className="w-6 text-center font-bold text-foreground tabular-nums">{quantity}</span>
                    <button
                      onClick={() => setQuantity(Math.min(4, quantity + 1))}
                      disabled={quantity >= 4}
                      className="w-8 h-8 rounded-lg border border-border flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40 transition-all"
                      aria-label="Increase quantity">
                      
                      <Plus size={14} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Order summary */}
              <div className="p-5 border-b border-border">
                <div className="flex flex-col gap-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{quantity} × {ticketType === 'vip' ? 'VIP' : 'General'}</span>
                    <span className="font-semibold tabular-nums">${price * quantity}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Platform fee</span>
                    <span className="font-semibold tabular-nums">${(quantity * price * 0.05).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-border font-bold text-base">
                    <span>Total</span>
                    <span className="tabular-nums">${(total + total * 0.05).toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <div className="p-5">
                {event.seatsRemaining === 0 ?
                <button disabled className="w-full py-3.5 bg-muted text-muted-foreground font-semibold rounded-xl cursor-not-allowed text-sm">
                    Sold Out
                  </button> :

                <button
                  onClick={handleRegister}
                  disabled={isRegistering}
                  className="w-full py-3.5 bg-primary text-primary-foreground font-bold rounded-xl hover:opacity-90 active:scale-95 transition-all duration-150 flex items-center justify-center gap-2 text-sm disabled:opacity-70">
                  
                    {isRegistering ?
                  <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> :

                  <><Ticket size={16} />Register & Pay</>
                  }
                  </button>
                }
                <p className="text-xs text-muted-foreground text-center mt-3">Secure checkout · Instant confirmation</p>
                <div className="flex items-center gap-2 mt-3 p-3 bg-warning-bg rounded-xl">
                  <span className="text-warning text-xs font-semibold">⚡ Only {event.seatsRemaining} seats left!</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Related events */}
      {relatedEvents.length > 0 &&
      <div className="mt-14">
          <h2 className="text-xl font-bold text-foreground mb-6">More {event.category}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {relatedEvents.map((re) =>
          <Link key={re.id} href="/event-detail" className="group block bg-card rounded-2xl border border-border overflow-hidden card-hover shadow-card">
                <div className="relative h-40 overflow-hidden">
                  <AppImage
                src={re.imageUrl}
                alt={`${re.title} event at ${re.venue}`}
                fill
                className="object-cover group-hover:scale-105 transition-transform duration-300"
                sizes="(max-width: 1024px) 50vw, 33vw" />
              
                </div>
                <div className="p-4">
                  <h3 className="font-bold text-foreground text-sm line-clamp-2 group-hover:text-primary transition-colors mb-1">{re.title}</h3>
                  <p className="text-xs text-muted-foreground mb-3">{re.city}</p>
                  <div className="flex items-center justify-between">
                    <span className="font-extrabold text-foreground tabular-nums">${re.ticketPrice}</span>
                    <StatusBadge status={re.status} />
                  </div>
                </div>
              </Link>
          )}
          </div>
        </div>
      }

      {/* Registration success modal */}
      {showModal &&
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm fade-in" onClick={() => setShowModal(false)}>
          <div className="bg-card rounded-3xl shadow-modal p-8 max-w-md w-full text-center fade-in" onClick={(e) => e.stopPropagation()}>
            <div className="w-16 h-16 rounded-full bg-success-bg flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 size={32} className="text-success" />
            </div>
            <h2 className="text-xl font-extrabold text-foreground mb-2">You&apos;re registered!</h2>
            <p className="text-sm text-muted-foreground mb-6">
              Your {quantity} × {ticketType === 'vip' ? 'VIP' : 'General'} ticket{quantity > 1 ? 's' : ''} for{' '}
              <strong>{event.title}</strong> have been confirmed. Check your email for the ticket PDF.
            </p>
            <div className="p-4 bg-muted rounded-xl mb-6 text-left">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-muted-foreground">Ticket type</span>
                <span className="font-semibold">{ticketType === 'vip' ? 'VIP' : 'General Admission'}</span>
              </div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-muted-foreground">Date</span>
                <span className="font-semibold">{formatDate(event.date)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Total paid</span>
                <span className="font-bold tabular-nums">${(total + total * 0.05).toFixed(2)}</span>
              </div>
            </div>
            <div className="flex gap-3">
              <Link
              href="/attendee-dashboard"
              className="flex-1 py-3 bg-primary text-primary-foreground font-semibold rounded-xl hover:opacity-90 transition-all text-sm">
              
                View My Tickets
              </Link>
              <button
              onClick={() => setShowModal(false)}
              className="px-4 py-3 border border-border rounded-xl text-sm font-semibold text-muted-foreground hover:bg-muted transition-all">
              
                <X size={16} />
              </button>
            </div>
          </div>
        </div>
      }
    </div>);

}